/* eslint-disable no-console */
import { ActionCreator } from '@reduxjs/toolkit';
import { likeToggled } from 'store/actions/like-service.actions';
import { buyButtonClicked, checkoutStarted, makeOfferButtonClicked } from 'store/actions/product-details.actions';
import { GTM, GTMEvents, PIXEL } from 'utils/analytics';
import { Braze, BrazeEvents } from 'utils/braze';

import { ActionHandler, createMiddleware } from './createMiddleware';

/**
 * GA `click_like_button` event emitted when user likes a product..
 * Provides the product ID as a parameter..
 */
const likeClickHandler: ActionHandler<ReturnType<typeof likeToggled>> = (_store, _next, action) => {
  const { id, liked } = action.payload;

  if (liked) {
    GTM.clickLikeButton(id);
  }
};

/**
 * GA `click_buy_button` event emitted when user clicks the Buy button for a product.
 * Provides the product ID as a parameter..
 */
const buyClickHandler: ActionHandler<ReturnType<typeof buyButtonClicked>> = (_store, _next, action) => {
  const product = action.payload;

  if (product.price) {
    PIXEL.trackCustom('UserClicksBuy', { product: product.slug });
  } else {
    PIXEL.trackCustom('UserClicksBuyFree', { product: product.slug });
  }

  GTM.clickBuyButton(product.id);
};

/**
 * GA `click_make_an_offer_button` event emitted when user clicks the Make an offer button for a product.
 * Provides the product ID as a parameter..
 */
const makeOfferClickHandler: ActionHandler<ReturnType<typeof makeOfferButtonClicked>> = (_store, _next, action) => {
  const product = action.payload;

  GTM.clickMakeAnOfferButton(product.id);
};

/**
 * Track when user lands on checkout page.
 * Sends events to all analytics providers: GTM, Braze.
 */
const checkoutStartedHandler: ActionHandler<ReturnType<typeof checkoutStarted>> = (_store, _next, action) => {
  const { product, shopSlug, checkoutVisitId, snapshot } = action.payload;

  // Track to GTM — flat displayed-state snapshot params only (DEV-2119); the snapshot carries
  // product_slug and checkout_visit_id, and item_price replaces the legacy price param. Rides
  // the dedicated snapshot path so logEvent's extra shared props don't breach the 25-param cap.
  GTM.logCheckoutSnapshotEvent(GTMEvents.CHECKOUT_START, { ...snapshot });
  GTM.addToCart(product);

  // Track to Braze (respects marketing consent)
  Braze.logCustomEvent(BrazeEvents.CHECKOUT_STARTED, {
    product_id: product.id,
    product_slug: product.slug,
    product_brand: product.brand,
    product_description: product.description || undefined,
    image_url: product.images[0]?.gallery || product.images[0]?.thumbnail || undefined,
    product_price: product.price,
    currency: product.currency,
    shop_slug: shopSlug,
    shop_id: product.shopId,
    checkout_visit_id: checkoutVisitId,
  });
};

const mapping: [ActionCreator<unknown> | string, ActionHandler<unknown>][] = [
  [likeToggled, likeClickHandler],
  [buyButtonClicked, buyClickHandler],
  [makeOfferButtonClicked, makeOfferClickHandler],
  [checkoutStarted, checkoutStartedHandler],
];

export default createMiddleware(new Map(mapping));
